import { Box } from "@mui/material";
import CustomDataGridV2 from "../../../../components/CustomDataGridV2";
import ToggleSwitch from "../../../../components/ToggleSwitch";
import {
  DEFAULT_PAGE_SIZE,
  applyDefaultColumnSettings,
} from "../../../../utils/utils";
import { useParams } from "react-router-dom";
import { Department } from "../../../../constants/ProviderLocationFromInitialValues";
import { useStatusDepartMent } from "../../../../queries/use-status-department";
import { useQueryClient } from "@tanstack/react-query";
import { useDepartmentList } from "../../../../queries/use-get-departments";
import { getViewAs, getStoredRealm } from "@/utils/auth-storage";

interface LocationDepartmentProps {
  searchKey: string;
  locationUuid?: string;
}

const LocationDepartment = ({
  searchKey,
  locationUuid,
}: LocationDepartmentProps) => {
  const params = useParams();

  const viewAs = getViewAs();
  const realm = getStoredRealm();
  const xTENANTID =
    params?.providerGroupId ?? (viewAs === "provider" ? realm : undefined);
  const { data } = useDepartmentList({
    pagination: {
      page: 0,
      pageSize: DEFAULT_PAGE_SIZE,
    },
    searchKey,
    providerGroupId: xTENANTID,
    locationUuid,
  });
  const departmentData = data?.data?.content;

  const COLUMNS = applyDefaultColumnSettings<Department>([
    {
      field: "deptId",
      flex: 0.5,
      minWidth: 150,
      headerName: "Department ID",
    },
    {
      field: "name",
      flex: 1,
      minWidth: 280,
      headerName: "Department Name",
    },
    {
      field: "adminName",
      flex: 1,
      minWidth: 190,
      headerName: "Department Admin",
    },
    {
      field: "contact",
      flex: 0.8,
      minWidth: 180,
      headerName: "Contact Number",
    },
    {
      field: "active",
      flex: 0.5,
      width: 150,
      headerName: "Status",
      renderCell: ({ row, value }) => (
        <ToggleSwitch
          id={row.uuid.toString()}
          checked={value}
          onConfirm={(v, id) => toggleStatus(v, id)}
        />
      ),
    },
  ]);

  const { mutateAsync: statusChange } = useStatusDepartMent();
  const queryClient = useQueryClient();

  function toggleStatus<T>(value: boolean, id: T) {
    return statusChange({
      depId: id?.toString() ?? "",
      status: value,
      xTENANTID: params?.providerGroupId,
    }).then((res) => {
      queryClient?.invalidateQueries({
        queryKey: ["provider-group-departments-list"],
      });
      return res;
    });
  }

  return (
    <Box sx={{ height: "calc(100vh - 7rem - 200px)" }}>
      <CustomDataGridV2
        searchString={searchKey}
        rows={departmentData ?? []}
        getRowId={(row) => row?.uuid}
        columns={COLUMNS}
        disableRowSelectionOnClick
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
        }
      />
    </Box>
  );
};

export default LocationDepartment;
